// The bundle manifest the edge serves at GET /v1/catalogue/manifest (the druif exporter's
// manifest.json, apps/druif/internal/catalogue/pack.go BundleManifest) and what a device decides
// from it: whether this build can read the catalogue, which file to fetch, and whether it is newer
// than the one installed.
import { CATALOGUE_SILENT_UPDATE_MAX_BYTES } from "@proef/contract";
import { z } from "zod";
import { call } from "../edgeApi";
import { newBrotliDecoder } from "./brotli";
import { cdnFileUrl, edgeFileUrl, rememberCdnBase } from "./cdn";
import { FOLD_VERSION } from "./fold";
import { DELTA_FORMAT, READER_SCHEMA } from "./errors";
import type { DeltaPlan, InstalledRef, InstallPlan } from "./protocol";

/** Names and versions end up in a URL path, so only plain file-name characters are accepted. */
const SAFE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

const BundleFile = z.object({
  name: z.string().regex(SAFE_NAME),
  sha256: z.string().regex(/^[0-9a-fA-F]{64}$/),
  bytes: z.number().int().nonnegative(),
  encoding: z.string().optional(),
  decoded: z.string().regex(SAFE_NAME).optional(),
});

/** One increment, as the exporter lists it in `deltas` (apps/druif/internal/catalogue/pack.go BundleDelta). */
const BundleDelta = z.object({
  from: z.string().regex(SAFE_NAME),
  to: z.string().regex(SAFE_NAME),
  name: z.string().regex(SAFE_NAME),
  sha256: z.string().regex(/^[0-9a-fA-F]{64}$/),
  content_sha256: z.string().regex(/^[0-9a-fA-F]{64}$/),
  bytes: z.number().int().nonnegative(),
  decoded_bytes: z.number().int().nonnegative(),
  format: z.number().int(),
});
export type BundleDelta = z.infer<typeof BundleDelta>;

export const BundleManifest = z.object({
  catalogue_version: z.string().regex(SAFE_NAME),
  built_at: z.string(),
  files: z.array(BundleFile),
  content_sha256: z
    .string()
    .regex(/^[0-9a-fA-F]{64}$/)
    .optional(),
  /** The chain of increments ending at `catalogue_version`, oldest first; absent from older exporters. */
  deltas: z.array(BundleDelta).optional(),
  /** The bucket's custom domain the edge adds when it has one; versioned files are fetched there first. */
  cdn_base: z.url({ protocol: /^https$/ }).optional(),
  schema_version: z.number().int(),
  min_reader_schema: z.number().int(),
  fold_version: z.number().int(),
});
export type BundleManifest = z.infer<typeof BundleManifest>;
export type BundleFile = z.infer<typeof BundleFile>;

/** Why a manifest cannot be installed by this build. */
export type Incompatibility = "app-too-old" | "no-usable-file";

/**
 * The edge's manifest. `no-cache`, not `no-store`: the browser revalidates with the ETag it saved, so
 * a night without changes costs an empty 304 and no body, while a new manifest comes through at once.
 */
export async function fetchManifest(): Promise<BundleManifest> {
  const manifest = await call(BundleManifest, "/v1/catalogue/manifest", { cache: "no-cache" });
  rememberCdnBase(manifest.cdn_base);
  return manifest;
}

/** The brotli decoder the browser ships, if any: without it the plain .sqlite is downloaded instead. */
export function brotliSupported(): boolean {
  try {
    newBrotliDecoder();
    return true;
  } catch {
    return false;
  }
}

/** Where to fetch a version's file: the CDN with the edge as fallback, or the edge alone. */
function sourcesOf(
  manifest: BundleManifest,
  version: string,
  name: string,
): { url: string; fallbackUrl?: string } {
  const edge = edgeFileUrl(version, name) ?? `/v1/catalogue/${version}/${name}`;
  const cdn = cdnFileUrl(manifest.cdn_base, version, name);
  return cdn === undefined ? { url: edge } : { url: cdn, fallbackUrl: edge };
}

/**
 * What to download from `manifest`, or why nothing can be. A catalogue this build cannot read (a
 * newer layout, other fold rules) asks for an app update rather than being half-understood.
 */
export function planInstall(
  manifest: BundleManifest,
  canBrotli: boolean,
): InstallPlan | Incompatibility {
  if (manifest.min_reader_schema > READER_SCHEMA || manifest.fold_version !== FOLD_VERSION)
    return "app-too-old";
  const packed = manifest.files.find((f) => f.encoding === "br" && f.decoded !== undefined);
  const raw = manifest.files.find(
    (f) =>
      f.encoding === undefined &&
      (packed === undefined ? f.name === "catalogue.sqlite" : f.name === packed.decoded),
  );
  const base = { version: manifest.catalogue_version, builtAt: manifest.built_at };
  if (canBrotli && packed !== undefined) {
    return {
      ...base,
      ...sourcesOf(manifest, base.version, packed.name),
      sha256: packed.sha256.toLowerCase(),
      bytes: packed.bytes,
      encoding: "br",
      ...(raw === undefined ? {} : { decodedBytes: raw.bytes }),
    };
  }
  if (raw === undefined) return "no-usable-file";
  return {
    ...base,
    ...sourcesOf(manifest, base.version, raw.name),
    sha256: raw.sha256.toLowerCase(),
    bytes: raw.bytes,
    encoding: "identity",
    decodedBytes: raw.bytes,
  };
}

/** The most increments one update may chain; the exporter keeps fewer, this only stops a malformed loop. */
const MAX_CHAIN = 64;

/** Why a device is not updating from deltas: what it then does is in `decideUpdate`. */
export type FullReason = "first-install" | "no-chain" | "too-large";

/**
 * The increments from `installedVersion` to the manifest's version, or undefined when there is no
 * unbroken chain this build can apply (an exporter without deltas, a gap, another delta layout, a
 * chain whose last digest disagrees with the manifest's).
 */
export function planDelta(
  manifest: BundleManifest,
  installedVersion: string,
): DeltaPlan | undefined {
  const byFrom = new Map<string, BundleDelta>();
  for (const delta of manifest.deltas ?? [])
    if (!byFrom.has(delta.from)) byFrom.set(delta.from, delta);
  const steps: BundleDelta[] = [];
  let at = installedVersion;
  while (at !== manifest.catalogue_version) {
    const next = byFrom.get(at);
    if (next === undefined || next.format !== DELTA_FORMAT || steps.length >= MAX_CHAIN)
      return undefined;
    steps.push(next);
    at = next.to;
  }
  const last = steps.at(-1);
  if (last === undefined) return undefined;
  const contentSha256 = last.content_sha256.toLowerCase();
  if (
    manifest.content_sha256 !== undefined &&
    manifest.content_sha256.toLowerCase() !== contentSha256
  )
    return undefined;
  return {
    version: manifest.catalogue_version,
    builtAt: manifest.built_at,
    steps: steps.map((s) => ({
      from: s.from,
      to: s.to,
      ...sourcesOf(manifest, s.to, s.name),
      sha256: s.sha256.toLowerCase(),
      bytes: s.bytes,
    })),
    bytes: steps.reduce((sum, s) => sum + s.bytes, 0),
    contentSha256,
  };
}

/** What a check of the edge's manifest leads to; the manager acts on it. */
export type UpdateDecision =
  | { kind: Incompatibility }
  | { kind: "current" }
  | { kind: "delta"; plan: DeltaPlan; full: InstallPlan | Incompatibility }
  | { kind: "full"; plan: InstallPlan; reason: FullReason };

/**
 * The update policy, in one place. Nothing to do when the installed version is the manifest's. When
 * an unbroken chain of increments exists and its total download is under
 * CATALOGUE_SILENT_UPDATE_MAX_BYTES, the update is a delta, applied without asking. Otherwise (no
 * catalogue yet, no chain, or a chain of 5 MB or more) it is the full bundle, which the person is
 * asked about. `full` rides along with a delta so a failed apply can fall back to it.
 */
export function decideUpdate(
  manifest: BundleManifest,
  installed: InstalledRef | undefined,
  canBrotli: boolean,
  silentMaxBytes: number = CATALOGUE_SILENT_UPDATE_MAX_BYTES,
): UpdateDecision {
  const full = planInstall(manifest, canBrotli);
  if (typeof full === "string") return { kind: full };
  if (installed === undefined) return { kind: "full", plan: full, reason: "first-install" };
  if (installed.version === full.version) return { kind: "current" };
  const delta = planDelta(manifest, installed.version);
  if (delta === undefined) return { kind: "full", plan: full, reason: "no-chain" };
  if (delta.bytes >= silentMaxBytes) return { kind: "full", plan: full, reason: "too-large" };
  return { kind: "delta", plan: delta, full };
}
